<template>
	<div id="add-marks">
		<!-- top row -->
		<div class="d-flex flex-wrap flex-sm-row flex-column-reverse align-sm-center">
			<!-- breadcrumbs -->
			<bee-breadcrumbs
				:items="[
					{
						to: {
							name: 'marks',
							params: {
								classId: $route.params.classId,
								courseId: $route.params.courseId,
							},
							query: { conceptId: $route.query.conceptId }
						},
						disabled: false,
						icon: 'mdi-bookshelf',
						text: 'علامات'
					},
					{
						to: { name: 'marks', params: { classId: $route.params.classId } },
						disabled: false,
						text: getClassById(+$route.params.classId)?.name
					},
					{
						to: {
							name: 'student-marks',
							params: {
								classId: $route.params.classId,
								courseId: $route.params.courseId,
								studentId: $route.params.studentId
							},
							query: { conceptId: $route.query.conceptId }
						},
						disabled: false,
						text: 'علامات طالب'
					},
					{
						to: {},
						disabled: true,
						text: 'تعديل العلامات'
					},
				]"
				class="mt-sm-0 mt-3"
			/>

			<v-spacer/>

			<!-- submit -->
			<v-btn
				color="primary"
				height="30"
				class="me-2"
				depressed
				:disabled="loading"
				:loading="submitLoading"
				@click="submit"
			>
				حفظ العلامات
			</v-btn>

			<!-- back -->
			<v-btn
				depressed
				height="34"
				@click="$router.go(-1)"
			>
				عودة
			</v-btn>
		</div>

		<v-divider class="my-3"/>

		<!-- loading -->
		<div
			v-if="loading"
			class="fill-height d-flex justify-center align-center"
			:style="`height:${$vuetify.breakpoint.height - 200}px`"
		>
			<v-progress-circular
				color="primary"
				size="40"
				indeterminate
			/>
		</div>

		<!-- table -->
		<div v-else>
			<p class="font-weight-bold mb-3">
				جدول علامات الطالب ( <span class="primary--text">{{student.name}}</span> )
			</p>

			<v-form ref="form" :disabled="submitLoading" @submit.prevent="submit">
				<!-- for submit purpose -->
				<v-btn v-show="false" type="submit"/>

				<!-- table -->
				<bee-handy-table
					:headers="headers"
					:items="items"
					:loading="loading || submitLoading"
					:height="$vuetify.breakpoint.height - 160"
					dense
					zebra
					fixed-header
					hide-default-footer
					:items-per-page="-1"
					shown-rows-text
					with-columns-divider
					table-class="overflow-hidden"
					:data-table-props="{ disableSort: true }"
				>
					<template v-slot:item="{ item, index }">
						<tr
							:class="
								index % 2 === 1
									? getConcept(item.conceptId, 'colorClass') + '-light'
									: 'bee-handy-table--zebra-color'
							"
						>
							<template v-for="(header, i) in headers">
								<!-- conceptId -->
								<template v-if="header.name === 'conceptId'">
									<td
										v-if="index === 0 || items[index - 1]?.conceptId !== item.conceptId"
										:class="[
											...getBindRowClass(header),
											getConcept(item.conceptId, 'colorClass')
										]"
										:rowspan="getConcept(item.conceptId, 'subRows').length"
										:key="i"
									>
										<div class="d-inline-flex pb-1 text-no-wrap rotate-header-90">
											{{item.conceptName}}
										</div>
									</td>
								</template>
								
								<!-- attendance -->
								<td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'attendance'
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<v-checkbox
										v-model="items[index].attendance"
										color="green darken-1"
										on-icon="$check"
										off-icon="$falseCheck"
										indeterminate-icon="$emptyCircleCheck"
										:indeterminate="items[index].attendance === null"
										:ripple="false"
										class="flat-checkbox mt-0"
										hide-details
										@change="updateRowData(index, true)"
									/>
								</td>

								<!-- submission date -->
								<!-- <td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'examDate'
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<bee-date-input
										v-model="items[index].examDate"
										hide-details
										label=" "
										dense
										user-menu-picker
										input-format-order="DD-MM-YYYY"
										prepend-icon="$calendar"
										:rules="rules.data"
									/>
								</td> -->

								<!-- max mark -->
								<td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'maxMark'
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									{{!item.scales.length ? items[index].maxMark : item.scales[item.scales.length - 1].code}}
								</td>

								<!-- marks obtained -->
								<td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'marksObtained' &&
										!item.scales.length
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<v-text-field
										v-model.number="items[index].mark"
										type="number"
										dense
										hide-spin-buttons
										hide-details
										:disabled="!items[index].attendance"
										:rules="
											items[index].attendance
												? [...rules.requiredAsNumber, val => val <= items[index].maxMark]
												: []
										"
										@input="updateRowData(index)"
									/>
								</td>

								<!-- marks obtained percentage -->
								<!-- <td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'marksObtainedPercentage'
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<v-text-field
										v-model.number="items[index].marksObtainedPercentage"
										type="number"
										dense
										hide-spin-buttons
										hide-details
										suffix="%"
										:rules="rules.percent"
									/>
								</td> -->

								<!-- marks obtained grade -->
								<td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'marksObtainedGrade' &&
										item.scales.length
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<v-autocomplete
										v-model="items[index].mark"
										:items="item.scales"
										item-text="code"
										item-value="orderIndex"
										dense
										hide-spin-buttons
										hide-details
										:disabled="!items[index].attendance"
										:rules="true ? rules.requiredAsNumber : []"
										@change="updateRowData(index)"
									/>
								</td>

								<!-- notes -->
								<td
									v-else-if="
										typeof item.markId === 'number' &&
										header.name === 'notes'
									"
									:key="i"
									:class="getBindRowClass(header)"
								>
									<v-text-field
										v-model="items[index].notes"
										dense
										hide-spin-buttons
										hide-details
									/>
								</td>
								
								<!-- other headers -->
								<td
									v-else
									:key="i"
									:class="getBindRowClass(header)"
								>
									{{
										item[header.value] ||
										(
											typeof item.markId !== 'number' &&
											typeof item[header.value] !== 'number' ? '-' : item[header.value]
										)
									}}
								</td>
							</template>
						</tr>
					</template>
				</bee-handy-table>
			</v-form>
		</div>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules';
import { fixNumber, getLateralGrade } from '@/helpers/functions';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
	name: 'UpdateStudentMarks',

	data: () => ({
		// helpers
		rules,
		items: [],

		loading: false,
		submitLoading: false,

		courseId: null,
		student: {},
		subjectRows: [],
	}),

	computed: {
		...mapGetters({
			getClassById: 'classes/getClassById',
			getCourseById: 'classes/getCourseById',
		}),

		headers() {
			const headers = [
				{
					text: '',
					name: 'conceptId',
					value: 'conceptId',
					width: 40,
				},
				{
					text: 'المادة',
					name: 'subjectName',
					value: 'subjectName',
					width: 200,
					divider: false,
				},
				{
					text: 'الحضور',
					name: 'attendance',
					value: 'attendance',
					width: 40,
					editable: true,
				},
				{
					text: 'تاريخ الامتحان',
					name: 'examDate',
					value: 'examDate',
					width: 200,
					editable: false,
				},
				{
					text: 'العلامة العظمى',
					name: 'maxMark',
					value: 'maxMark',
					width: 120,
					editable: false,
				},
				{
					text: 'العلامة المستحقة\n(رقماً)',
					name: 'marksObtained',
					value: 'marksObtained',
					width: 120,
					editable: true,
				},
				{
					text: 'العلامة المستحقة\n(نسبة مئوية)',
					name: 'marksObtainedPercentage',
					value: 'marksObtainedPercentage',
					width: 120,
					editable: false,
				},
				{
					text: 'العلامة المستحقة\n(ترميز)',
					name: 'marksObtainedGrade',
					value: 'marksObtainedGrade',
					width: 120,
					editable: false,
				},
				{
					text: 'ملاحظات',
					name: 'notes',
					value: 'notes',
					editable: true,
				}
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' color-1' : 'color-1';
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
			}
			return headers;
		},

		nestedRows() {
			/* const headers = [
				{
					text: 'Reading',
					id: 'reading',
					subRows: [
						{
							text: 'Reading in phrases',
							name: 'readingInPhrases',
							value: 'readingInPhrases',
						},
						{
							text: 'Pace',
							name: 'pace',
							value: 'pace',
						},
						{
							text: 'Syntax',
							name: 'syntax',
							value: 'syntax',
						},
						{
							text: 'Self-corrections',
							name: 'selfCorrections',
							value: 'selfCorrections',
						},
						{
							text: 'Intonation',
							name: 'intonation',
							value: 'intonation',
						},
						{
							text: 'Letter naming fluency',
							name: 'letterNamingFluency',
							value: 'letterNamingFluency',
						},
						{
							text: 'Slight word fluency',
							name: 'slightWordFluency',
							value: 'slightWordFluency',
						},
						{
							text: 'Comprehension',
							name: 'comprehension',
							value: 'comprehension',
						},
					]
				},
				{
					text: 'Phonics awareness',
					id: 'phonicsAwareness',
					subRows: [
						{
							text: 'Phoneme categorization',
							name: 'phonemeCategorization',
							value: 'phonemeCategorization',
						},
						{
							text: 'Phoneme blending',
							name: 'phonemeBlending',
							value: 'phonemeBlending',
						},
						{
							text: 'Phoneme segmentation',
							name: 'phonemeSegmentation',
							value: 'phonemeSegmentation',
						},
					],
				},
				{
					text: 'Readers to writers',
					id: 'readersToWriters',
					subRows: [
						{
							text: 'Spelling',
							name: 'spelling',
							value: 'spelling',
						},
						{
							text: 'Vocabulary',
							name: 'vocabulary',
							value: 'vocabulary',
						},
						{
							text: 'Ideas and content',
							name: 'ideasAndContent',
							value: 'ideasAndContent',
						},
						{
							text: 'Grammar',
							name: 'grammar',
							value: 'grammar',
						},
						{
							text: 'Structural analysis',
							name: 'structuralAnalysis',
							value: 'structuralAnalysis',
						},
						{
							text: 'Writing',
							name: 'writing',
							value: 'writing',
						},
					],
				},
				{
					text: 'Listening and speaking',
					id: 'listeningAndSpeaking',
					subRows: [
						{
							text: 'Interactive read aloud',
							name: 'interactiveReadAloud',
							value: 'interactiveReadAloud',
						},
						{
							text: 'Retelling cards',
							name: 'retellingCards',
							value: 'retellingCards',
						},
					],
				},
				{
					text: 'Social and study skills',
					id: 'socialAndStudySkills',
					subRows: [
						{
							text: 'Participation',
							name: 'participation',
							value: 'participation',
						},
						{
							text: 'Completion of work',
							name: 'completionOfWork',
							value: 'completionOfWork',
						},
						{
							text: 'Concentration',
							name: 'concentration',
							value: 'concentration',
						},
						{
							text: 'Self direction',
							name: 'selfDirection',
							value: 'selfDirection',
						},
						{
							text: 'Cooperation with others',
							name: 'cooperationWithOthers',
							value: 'cooperationWithOthers',
						},
						{
							text: 'Behavior',
							name: 'behavior',
							value: 'behavior',
						},
						{
							text: 'Presentation of assignments',
							name: 'presentationOfAssignments',
							value: 'presentationOfAssignments',
						},
					],
				},
				{
					text: 'Movement video',
					id: 'movementVideo',
					subRows: [
						{
							text: null,
							name: 'movementVideo',
							value: 'movementVideo',
							noRotate: true
						}
					]
				}
			]; */
			const headers = this.subjectRows;

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-0' : 'px-0';
				headers[i].colorClass = `color-${((i) % 6) + 1}`;
				headers[i].align = 'center';
			}
			return headers;
		},

		marksChartColors() {
			const colors = []
			this.nestedRows.forEach((row, i) => {
				if (row.subRows) {
					row.subRows.forEach(() => {
						colors.push(this.baseColors[i % this.baseColors.length])
					})
				}
			})
			return colors;
		},
	},

	methods: {
		getConcept(conceptId, propSearch = null) {
			const concept = this.nestedRows.find(c => c.id === conceptId)
			if (concept) {
				if (propSearch) return concept[propSearch] || null;
				return concept;
			}
			return {};	
		},

		getBindRowClass(header) {
			return [
				header.cellClass,
				header.columnClass,
				{ 'v-data-table__divider': header.divider },
				`text-${header.align || 'start'}`,
			]
		},

		fillItemsItems() {
			const flattedRows = this.nestedRows.flatMap(row => {
				if (row.subRows) {
					return row.subRows.map(child => ({
						conceptId: row.id,
						conceptName: row.text,
						...child,
					}))
				}
				return row;
			});

			// generate values
			/* this.items = flattedRows.flatMap(row => {
				return this.headers.reduce((res, a, i, arr) => {
					if (!(a.value in res) && i !== arr.length) {
						if (a.value === 'attendance') {
							res[a.value] = Math.random() > 0.5;
						} else if (a.value === 'examDate') {
							res[a.value] = new Date().toISOString().slice(0, 10);
						} else {
							res[a.value] = Math.round(Math.random() * 100);
						}
					}
					
					return res;
				}, {
					conceptId: row.conceptId,
					conceptName: row.conceptName,
					subjectName: row.text,
					...row
				}) */
			this.items = flattedRows.flatMap(row => ({
				conceptId: row.conceptId,
				conceptName: row.conceptName,
				subjectName: row.text,
				...row
			}))
		},

		updateRowData(index, isClear = false) {
			const item = this.items[index];
			if (!isClear) {
				const markPercentage = fixNumber((item.mark / (item.maxMark || 1)) * 100, 2) 
				this.items[index].marksObtained = item.mark || 0;
				this.items[index].marksObtainedPercentage = markPercentage + '%';
				this.items[index].marksObtainedGrade =
					!item.scales.length
						? getLateralGrade(markPercentage || 0).code
						: item.scales.find(e => e.orderIndex === item.mark)?.code ??
							getLateralGrade(markPercentage || 0).code;
			} else {
				this.items[index].mark = !this.items[index].scales.length ? 0 : null;
				this.items[index].marksObtained = 0;
				this.items[index].marksObtainedPercentage = '0%';
				this.items[index].marksObtainedGrade = 0;
			}
		},

		// fetch
		fetchClassData() {
			return this.$store.dispatch('classes/fetchClassData', {
				classId: this.$route.params.classId
			});
		},

		fetchSubjects(data) {
			if (!this.$route.params.courseId) return;
			let promise;

			const getMainData = subjectId => data.find(c => c.subjectId === subjectId) || {};
			
			if (!this.subjectRows.length) {
				promise = this.$store.dispatch('subjects/fetchByClassId', {
					classId: this.$route.params.classId
				}).then(data => {
					this.subjectRows = data.map(c => ({
						id: c.id,
						text: c.name,
						subRows: c.subjectChilds.map(e => ({
							text: e.name,
							...getMainData(e.id),
						}))
					}));
				})
			} else {
				this.subjectRows = this.subjectRows.map(c => ({
					id: c.id,
					text: c.text,
					subRows: c.subRows.map(e => ({
						text: e.text,
						...getMainData(e.subjectId),
					}))
				}));
				promise = Promise.resolve();
			}
			return promise;
		},

		async fetchMarks(preventLoading = false) {
			if (!this.$route.params.courseId) return;
			
			this.loading = !preventLoading;

			try {
				let data;
				await Promise.all([
					this.fetchClassData(),
					this.$store.dispatch('marks/fetchStudentMarks', { 
						studentId: this.$route.params.studentId,
						courseId: this.courseId,
						conceptId: this.conceptId,
					}).then(marksData => {
						data =
							Object.keys(marksData).length
								? marksData
								: { student: { username: 'الطالب' }, examTest: [] };
					})
				]);
				
				this.student = {
					id: data.student.id,
					name: data.student.username,
					marksData: data.examTest.flatMap(examTest => 
						(examTest.examGrades || []).map(c => {
							const markPercentage =
								c.grade ? fixNumber((c.grade.mark / (c.topicMaxMark || 1)) * 100, 2) : null;
								
							return {
								examName: data.examTest[0].name,
								markId: c.examTopicId,
								subjectId: c.topic.id,
								subjectName: c.topic.name,
								subjectGroupName: c.topic.parent.name,
								mark: c.grade ? c.grade.mark : null,
								maxMark: c.topicMaxMark,
								markPercentage,
								scales: (c.topic.scales || []).sort((a, b) => a.orderIndex - b.orderIndex),
								attendance: c.grade ? !c.grade.isAbsent : null,
								examDate: examTest.date ? moment(examTest.date).format('YYYY-MM-DD') : null,
								marksObtained: c.grade ? c.grade.mark : null,
								marksObtainedPercentage: c.grade ? markPercentage + '%' : null,
								marksObtainedGrade:
									c.grade
										? !c.topic.scales.length
											? getLateralGrade(markPercentage || 0).code
											: c.topic.scales.find(e => e.orderIndex === c.grade.mark)?.code ??
												getLateralGrade(markPercentage || 0).code
										: null,
								notes: c.grade ? c.grade.notes : null,
							}
						})
					)
				};
				
				await this.fetchSubjects(this.student.marksData);
				this.fillItemsItems(this.student.marksData);
			} catch (e) {
				console.error(e);
			} finally {
				this.loading = false;
			}
		},

		// submit
		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				this.$store.dispatch('marks/updateStudentMarks', {
					studentId: +this.$route.params.studentId,
					marks: this.items.map(c => ({
						examTopicId: c.markId,
						mark: c.mark,
						date: c.examDate,
						isAbsent: !c.attendance,
						notes: c.notes,
					})).filter(c => typeof c.mark === 'number'),
				}).then(() => {
					this.$snackbar.info('تم تحديث العلامات بنجاح');
					this.$router.push({
						name: 'student-marks',
						params: {
							classId: this.$route.params.classId,
							courseId: this.$route.params.courseId,
							studentId: this.$route.params.studentId
						},
						query: { conceptId: this.$route.query.conceptId }
					});
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		}
	},

	created() {
		this.courseId = +this.$route.params.courseId || null;
		this.fetchMarks();
	}
}
</script>

<style lang="scss">
#add-marks {
	.v-data-table-header th {
		white-space: pre-line;
	}
	.mdi-close-circle-outline {
		color: #ff3334;
	}
}
</style>